import React from "react";
import { motion } from "framer-motion";
import Footer from "./Footer";

const services = [
  {
    icon: "language",
    iconBg: "#FBF0E2",
    iconColor: "#C8762A",
    techHeading: "WEB DEVELOPMENT",
    title: "Custom Web Development",
    description:
      "Build modern, responsive, and scalable websites and enterprise web applications tailored to your business needs.",
    highlights: [
      "Responsive & SEO-friendly websites",
      "Scalable enterprise web applications",
    ],
    tags: ["React", "Next.js", "JavaScript"],
  },
  {
    icon: "flutter_dash",
    iconBg: "#E0F2FE",
    iconColor: "#0284C7",
    techHeading: "FLUTTER DEVELOPMENT",
    title: "Flutter App Development",
    description:
      "Develop beautiful, high-performance cross-platform mobile applications using a single Flutter codebase.",
    highlights: [
      "Single codebase for Android & iOS",
      "Fast development with Hot Reload",
    ],
    tags: ["Flutter", "Dart", "Firebase"],
  },
  {
    icon: "phone_iphone",
    iconBg: "#DCFCE7",
    iconColor: "#16A34A",
    techHeading: "REACT NATIVE",
    title: "React Native Development",
    description:
      "Create scalable mobile applications with React Native, delivering near-native performance across Android and iOS.",
    highlights: [
      "Reusable React components",
      "Native API integration",
    ],
    tags: ["React Native", "JavaScript", "Android & iOS"],
  },
  {
    icon: "coffee",
    iconBg: "#FEF3C7",
    iconColor: "#B45309",
    techHeading: "JAVA BACKEND",
    title: "Java & Spring Boot",
    description:
      "Develop secure enterprise applications, REST APIs, and cloud-ready microservices with Spring Boot.",
    highlights: [
      "REST APIs & Microservices",
      "Enterprise-grade architecture",
    ],
    tags: ["Java", "Spring Boot", "Hibernate"],
  },
  {
    icon: "dns",
    iconBg: "#DBEAFE",
    iconColor: "#2563EB",
    techHeading: "NODE.JS",
    title: "Node.js Development",
    description:
      "Build high-performance backend systems, REST APIs, and real-time applications using Node.js.",
    highlights: [
      "REST & GraphQL APIs",
      "Real-time applications with Socket.IO",
    ],
    tags: ["Node.js", "Express", "MongoDB"],
  },
  {
    icon: "shopping_bag",
    iconBg: "#DCFCE7",
    iconColor: "#15803D",
    techHeading: "SHOPIFY",
    title: "Shopify Store Development",
    description:
      "Launch high-converting Shopify stores with custom themes, app integrations, payment gateways, and performance optimization.",
    highlights: [
      "Custom Shopify themes",
      "Payment & app integrations",
    ],
    tags: ["Shopify", "Liquid", "E-commerce"],
  },
  {
    icon: "web",
    iconBg: "#DBEAFE",
    iconColor: "#2563EB",
    techHeading: "WORDPRESS",
    title: "WordPress Development",
    description:
      "Build secure, SEO-friendly, and easy-to-manage WordPress websites with custom themes and plugins.",
    highlights: [
      "Custom themes & plugins",
      "WooCommerce integration",
    ],
    tags: ["WordPress", "PHP", "WooCommerce"],
  },
  {
    icon: "draw",
    iconBg: "#EDE9FE",
    iconColor: "#7C3AED",
    techHeading: "UI / UX DESIGN",
    title: "UI & UX Design",
    description:
      "Create intuitive user experiences with research-driven wireframes, interactive prototypes, and modern design systems.",
    highlights: [
      "Wireframing & Prototyping",
      "Modern Design Systems",
    ],
    tags: ["Figma", "Adobe XD", "UX Research"],
  },
  {
    icon: "palette",
    iconBg: "#FCE7F3",
    iconColor: "#DB2777",
    techHeading: "GRAPHIC DESIGN",
    title: "Graphic Design",
    description:
      "Creative branding, marketing materials, social media creatives, brochures, and professional visual assets.",
    highlights: [
      "Social Media Creatives",
      "Brand Marketing Assets",
    ],
    tags: ["Photoshop", "Illustrator", "Canva"],
  },
  {
    icon: "auto_awesome",
    iconBg: "#FEF3C7",
    iconColor: "#CA8A04",
    techHeading: "BRANDING",
    title: "Brand Identity Design",
    description:
      "Develop memorable brand identities with logos, typography, color systems, and complete brand guidelines.",
    highlights: [
      "Logo & Identity Design",
      "Complete Brand Guidelines",
    ],
    tags: ["Logo", "Brand Strategy", "Identity"],
  },
  {
    icon: "cloud",
    iconBg: "#E0F2FE",
    iconColor: "#0284C7",
    techHeading: "CLOUD & DEVOPS",
    title: "Cloud & DevOps",
    description:
      "Deploy and manage scalable cloud infrastructure with automated CI/CD pipelines and containerized applications.",
    highlights: [
      "AWS & Azure Deployment",
      "Docker & CI/CD Pipelines",
    ],
    tags: ["AWS", "Docker", "Kubernetes"],
  },
  {
    icon: "trending_up",
    iconBg: "#DCFCE7",
    iconColor: "#16A34A",
    techHeading: "DIGITAL MARKETING",
    title: "SEO & Performance Optimization",
    description:
      "Increase your online visibility with technical SEO, performance optimization, and data-driven growth strategies.",
    highlights: [
      "Technical SEO",
      "Core Web Vitals Optimization",
    ],
    tags: ["SEO", "Analytics", "Performance"],
  },
];

function ServiceCard({ service, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 25 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.4,
        delay: Math.min(index * 0.05, 0.2),
      }}
      viewport={{
        once: true,
        amount: 0.1,
      }}
      className="
        w-full
        min-w-0
        rounded-2xl
        border border-[#E7DED0]
        bg-white
        p-5
        transition-all
        duration-300
        sm:p-6
        lg:p-8
        lg:hover:-translate-y-1
        lg:hover:shadow-2xl
      "
    >
      {/* Service Icon */}
      <div
        className="
          mb-5
          flex h-12 w-12
          items-center justify-center
          rounded-xl
          sm:mb-6
          sm:h-14 sm:w-14
        "
        style={{
          backgroundColor: service.iconBg,
          color: service.iconColor,
        }}
      >
        <span className="material-symbols-outlined text-[24px] sm:text-[28px]">
          {service.icon}
        </span>
      </div>

      {/* Technology Heading */}
      <p
        className="
          mb-2
          break-words
          text-[10px]
          font-semibold
          uppercase
          tracking-[1.5px]
          sm:mb-3
          sm:text-[11px]
          sm:tracking-[2px]
        "
        style={{ color: service.iconColor }}
      >
        {service.techHeading}
      </p>

      {/* Title */}
      <h3
        className="
          mb-3
          break-words
          text-lg
          font-bold
          leading-tight
          text-[#1A1714]
          sm:text-xl
        "
      >
        {service.title}
      </h3>

      {/* Description */}
      <p
        className="
          mb-5
          break-words
          text-sm
          leading-6
          text-[#6B6560]
          sm:mb-6
          sm:leading-7
        "
      >
        {service.description}
      </p>

      {/* Tags */}
      <div className="flex min-w-0 flex-wrap gap-2">
        {service.tags.map((tag) => (
          <span
            key={tag}
            className="
              max-w-full
              break-words
              rounded-full
              border border-[#E7DED0]
              bg-[#F7F4EE]
              px-2.5
              py-1
              text-[11px]
              font-medium
              text-[#6B6560]
              sm:px-3
              sm:text-xs
            "
          >
            {tag}
          </span>
        ))}
      </div>
    </motion.div>
  );
}

export default function Services() {
  return (
    <div className="w-full overflow-x-hidden">
      {/* Hero Banner */}
      <section
        className="
          relative
          mt-20
          h-[280px]
          w-full
          overflow-hidden
          sm:h-[340px]
          md:h-[420px]
        "
      >
        <img
          src="https://images.unsplash.com/photo-1518770660439-4636190af475?w=1600&q=80"
          alt="Our Services"
          className="h-full w-full object-cover object-center"
        />

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-[#1A1714]/75" />

        {/* Amber Glow */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 60% 50%, rgba(200,118,42,0.2) 0%, transparent 65%)",
          }}
        />

        {/* Hero Content */}
        <div
          className="
            absolute
            inset-0
            flex
            flex-col
            items-center
            justify-center
            px-5
            text-center
            sm:px-6
          "
        >
          {/* Small Heading */}
          <div className="mb-3 flex items-center justify-center gap-2 sm:mb-4">
            <span className="h-px w-5 bg-[#C8762A] sm:w-6" />

            <p
              className="
                text-[10px]
                font-bold
                uppercase
                tracking-widest
                text-[#C8762A]
                sm:text-[11px]
              "
            >
              What We Do
            </p>

            <span className="h-px w-5 bg-[#C8762A] sm:w-6" />
          </div>

          {/* Main Heading */}
          <h1
            className="
              mb-3
              max-w-[320px]
              text-[28px]
              font-bold
              leading-[1.15]
              text-white
              sm:max-w-xl
              sm:text-4xl
              md:mb-4
              md:text-5xl
            "
          >
            Mastering the{" "}
            <em className="italic text-[#C8762A]">
              Digital Spectrum
            </em>
          </h1>

          {/* Description */}
          <p
            className="
              max-w-[330px]
              text-sm
              leading-6
              text-white/70
              sm:max-w-[480px]
              sm:text-base
              sm:leading-relaxed
            "
          >
            From conceptual branding to robust engineering — integrated
            solutions that redefine user expectations.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section
        id="services"
        className="
          bg-[#F7F4EE]
          py-12
          sm:py-16
          lg:py-20
        "
      >
        <div
          className="
            mx-auto
            w-full
            max-w-7xl
            px-4
            sm:px-6
            lg:px-8
          "
        >
          <div
            className="
              grid
              grid-cols-1
              gap-4
              sm:grid-cols-2
              sm:gap-5
              lg:grid-cols-3
              lg:gap-6
            "
          >
            {services.map((service, index) => (
              <ServiceCard
                key={service.title}
                service={service}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}